import React, { useEffect, useRef } from "react"
import moment from "moment-timezone"
import { useDispatch } from "react-redux"
import { withChildPlace, withEntryDetails } from "../../../components/participantDisplay"
import { accountNumber, entryKeys, participantKey } from "../../../components/participantIdentity"
import ParticipantSummary from "../../../components/ParticipantSummary"
import { isSeeded } from "../../../components/seeds"
import Slot from "../../../components/Slot"
import { AddPlayer } from "./Match"
import { getMatches } from "../actions"

export const matchesSearch = (row, query) => {
  const search = query.trim().toLocaleLowerCase()
  if (!search) return false
  return ["player", "opponent", "playerPartner", "opponentPartner"].some(role => {
    const person = row[role]
    if (person?.userId == null) return false
    // RELEASE-TENIS10 § 11 P78: a child by its own number, never by its userId (its parent's)
    const number = accountNumber(person)
    return /^#\d+$/.test(search)
      ? number != null && String(number) === search.slice(1)
      : `${participantName(person)} ${number ?? ""}`.toLocaleLowerCase().includes(search)
  })
}
export const participantName = person =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") ||
  person?.type ||
  "Participant pending"
// W is a current registration indicator. Do not merge payment or ranking from a
// different registration into the historical match participant. The entry is the
// participant's own, by participant key: a Tenis10 child's — whose W the api computes
// for the child — never its parent's or a sibling's (PRD 16 LR11c). P73: a child's place, the same way.
const withEntryIndicator = (person, details) => {
  const key = participantKey(person)
  const entry = key && [
    ...(details.mainList || []),
    ...(details.waitingList || []),
  ].find(row => entryKeys(row).includes(key))
  const current =
    (person?.profileId || String(entry?.user?.userId) === String(person?.userId)) ? entry?.user : entry?.partner
  return withChildPlace({
    ...withEntryDetails(person, current),
    points: person?.userPoints,
    pointsDouble: undefined,
    w: person?.w === true || current?.w === true,
  }, details)
}
export const Schedule = ({ scheduler, timezone }) => {
  const format = (value, pattern) => {
    const date = moment(value)
    if (!date.isValid()) return "Date unavailable"
    return (
      timezone && moment.tz.zone(timezone) ? date.tz(timezone) : date
    ).format(pattern)
  }
  const parts = [
    scheduler?.playingDay && format(scheduler.playingDay, "ddd, DD MMM YYYY"),
    scheduler?.playingTime &&
      `Not before ${format(scheduler.playingTime, "HH:mm")}`,
    scheduler?.court,
  ].filter(Boolean)
  return (
    <div className="main-draw-schedule">
      {parts.length ? parts.join(" · ") : "Not scheduled"}
      {scheduler?.playingTime && timezone ? ` (${timezone})` : ""}
    </div>
  )
}
export const SetScore = ({ person, set }) => {
  const score = person?.score?.find(row => Number(row.set) === set)
  const value = score?.points ?? "not recorded"
  const tie = score?.tie != null && score.tie !== ""
  return (
    <span
      className="main-draw-set-score"
      aria-label={`${participantName(person)} · Set ${set}: ${value}${
        tie ? `, tie-break ${score.tie}` : ""
      }`}
    >
      {score?.points ?? "—"}
      {tie && <sup>{score.tie}</sup>}
    </span>
  )
}
const Outcome = ({ reason }) =>
  reason ? (
    <strong
      title={
        reason === "NO_SHOW"
          ? "Walkover: did not show up"
          : reason === "ABANDONED"
          ? "Abandoned"
          : reason
      }
    >
      {reason === "NO_SHOW" ? "WO" : reason === "ABANDONED" ? "Ab." : reason}
    </strong>
  ) : null
export const Scores = ({ person, noOfSets }) => (
  <div
    className="main-draw-score"
    aria-label={`Scores for ${participantName(person)}`}
  >
    {Array.from({ length: noOfSets || 0 }, (_, i) => (
      <SetScore key={i} person={person} set={i + 1} />
    ))}
    <Outcome reason={person?.reason} />
  </div>
)
// A later-round side with nobody in it (a swap or a Replace on a BYE place reopens one) waits for
// the match feeding it: only that result fills it, and Accounts refuses anyone added there and any
// score on the match (SLOT_WAITS_FOR_EARLIER_RESULT). The odd match of the previous round feeds the
// upper side.
export const waitsForEarlierResult = (row, person) =>
  row.competition?.drawType === "eliminatory" &&
  Number(row.competition?.stage) > 1 &&
  person?.userId == null &&
  !person?.type &&
  !person?.placeholder
export const feedingMatch = (row, role) =>
  Number(row.competition?.position) * 2 - (role === "player" ? 1 : 0)
// A result typed while a side waits would be lost when the feeding result rebuilds the match, and
// Accounts refuses it, so Edit match stays off (the card and the participant search alike).
export const sideWaits = row =>
  ["player", "opponent"].some(role => waitsForEarlierResult(row, row[role]))
const MatchScoreboard = ({ row }) => (
  <div className="main-draw-scoreboard">
    <div data-match-size="score">
      <table aria-label="Match score · top–bottom">
        <tbody>
          {["player", "opponent"].map(role => (
            <tr
              key={role}
              className={row.winner === role ? "text-success" : undefined}
              aria-label={
                row.winner === role
                  ? `Winner: ${participantName(row[role])}`
                  : undefined
              }
            >
              {Array.from({ length: row.noOfSets || 0 }, (_, i) => (
                <td key={i}>
                  <SetScore person={row[role]} set={i + 1} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)

export default function MatchCard({
  row,
  label,
  index,
  competitionDetails,
  eventDetails,
  setModal,
  matches,
  highlighted,
  qualification,
  qualifyingMatches = [],
  // a match named for what it decides (the Tenis10 small final) instead of its number
  title,
}) {
  const dispatch = useDispatch()
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const fetchMatches = payload => {
    if (mounted.current) dispatch(getMatches(payload))
  }
  if (row.type === "TBA")
    return (
      <article
        className="main-draw-match main-draw-tba"
        data-match-id={row._id}
        aria-label={`${label} · Match ${index + 1}`}
      >
        <strong>TBA</strong>
        <span>Participants to be determined</span>
      </article>
    )
  const gameType = row.competition?.gameType || competitionDetails.gameType
  const waiting = sideWaits(row)
  return (
    <article
      className={`main-draw-match${
        highlighted ? " main-draw-match--found" : ""
      }`}
      data-match-id={row._id}
      data-search-match={highlighted ? "true" : undefined}
      tabIndex={-1}
      aria-label={`${label} · Match ${row.competition?.position || index + 1}`}
    >
      <div className="main-draw-card-context">
        <div data-match-size="context">
          <header>
            <span>{title || `Match ${row.competition?.position || index + 1}`}</span>
            <button
              type="button"
              className="btn btn-sm btn-light"
              disabled={!row._id || waiting}
              onClick={() => setModal(row._id)}
            >
              Edit match
            </button>
          </header>
          <Schedule
            scheduler={row.scheduler}
            timezone={eventDetails?.timezone}
          />
          {qualification && (
            <div className="main-draw-provenance">
              Feeds first-round position{" "}
              {row.competition?.nextStagePosition ?? "—"}
            </div>
          )}
        </div>
      </div>
      {["player", "opponent"].map(role => {
        const person = row[role] || {},
          partner = row[`${role}Partner`]
        const qualifier =
          Number(row.competition?.stage) === 1 &&
          qualifyingMatches?.find(
            match =>
              Number(match.competition?.nextStagePosition) ===
              Number(row.competition?.position) * 2 -
                (role === "player" ? 1 : 0)
          )
        // P54: none at Tenis10 (seeds.js)
        const seeded = isSeeded(person, competitionDetails)
        // Who the admin sees in this side (null for an open place or a BYE). A change to the
        // side carries it, and Accounts refuses the change when the side holds someone else.
        const expected = {
          [role]: row[role]?.userId ?? null,
          [`${role}Partner`]: partner?.userId ?? null,
          // a Tenis10 child, never its sibling under the same parent account (PRD 16 LR11c)
          ...(row[role]?.profileId && { [`${role}ProfileId`]: row[role].profileId }),
        }
        return (
          <React.Fragment key={role}>
            {role === "opponent" && <MatchScoreboard row={row} />}
            <div
              className={`main-draw-side${
                row.winner === role ? " main-draw-side--winner" : ""
              }`}
            >
              <div className="main-draw-side-content" data-match-size="side">
                <div className="main-draw-side-heading">
                  {person.competitionRank != null && (
                    <span title="Main List order">ML #{person.competitionRank}</span>
                  )}
                  <Outcome reason={person.reason} />
                </div>
                {waitsForEarlierResult(row, row[role]) ? (
                  <div className="main-draw-participants">
                    <strong>Winner of match {feedingMatch(row, role)}</strong>
                  </div>
                ) : person.userId || ["BYE", "PENDING"].includes(person.type) ? (
                  <Slot
                    user={person}
                    partner={partner}
                    role={role}
                    expected={expected}
                    matchId={row._id}
                    competitionId={competitionDetails._id}
                    competitionDetails={competitionDetails}
                    mainList={competitionDetails.mainList || []}
                    gameType={gameType}
                    draw={row.competition?.drawType}
                    fetchMatches={fetchMatches}
                    renderCard={({ openReplacement }) => (
                      <div className="main-draw-participants">
                        {person.userId ? (
                          <ParticipantSummary
                            variant="draw"
                            seedRank={
                              seeded ? person.competitionRank ?? "—" : undefined
                            }
                            person={withEntryIndicator(
                              person,
                              competitionDetails
                            )}
                            gameType={gameType}
                            currency={eventDetails?.currency}
                            eventDetails={eventDetails}
                          />
                        ) : (
                          <strong>
                            {qualifier && person.type === "PENDING"
                              ? `Winner of qualifying match ${qualifier.competition.position}`
                              : person.type === "PENDING"
                              ? person.placeholder || "Qualifier pending"
                              : "BYE"}
                          </strong>
                        )}
                        {partner?.userId && (
                          <ParticipantSummary
                            variant="draw"
                            person={withEntryIndicator(
                              partner,
                              competitionDetails
                            )}
                            gameType={gameType}
                            currency={eventDetails?.currency}
                            eventDetails={eventDetails}
                          />
                        )}
                        <button
                          type="button"
                          className="btn btn-link btn-sm main-draw-replace"
                          onClick={openReplacement}
                        >
                          Replace {partner?.userId ? "team" : "player"}
                        </button>
                      </div>
                    )}
                  />
                ) : (
                  <AddPlayer
                    competitionDetails={competitionDetails}
                    currentEvent={eventDetails}
                    mainList={competitionDetails.mainList || []}
                    fetchMatches={fetchMatches}
                    index={index}
                    phaseLength={matches.length}
                    {...{ [role]: person }}
                    role={role}
                    expected={expected}
                    matchId={row._id}
                    matches={matches}
                  />
                )}
              </div>
            </div>
          </React.Fragment>
        )
      })}
    </article>
  )
}
